// 声网 Agora Web SDK 封装 - 语音通话专用。
// AgoraRTC 全局对象由 public/index.html 中的 script 标签引入(SDK 自托管在
// public/webrtc/ 下, 不走外部 CDN: 通话是关键路径, 不能把能不能打电话
// 押在一个外部域名的可达性上)。
// 用法与 public/webrtc/call.html(App 端已验证的权威实现)保持一致。

// 音频采集参数: speech_low_quality = 16kHz 单声道, 语音通话的常规档;
// AEC/ANS/AGC 全开, 与旧版 getUserMedia 的 echoCancellation/noiseSuppression 语义对齐
const AUDIO_CONFIG = {
	encoderConfig: 'speech_low_quality',
	AEC: true,
	ANS: true,
	AGC: true
};

// 自动播放失败时注册的 click 重试监听引用，leave() 时用于移除监听
let autoplayRetryHandler = null;

class AgoraVoiceClient {
	constructor() {
		this.client = null;
		this.localAudioTrack = null;
		this.remoteUsers = {};
		this.joined = false;
		// 事件回调(由组件设置)
		this.onRemoteUserJoined = null; // (user) => {}
		this.onRemoteUserLeft = null; // (user) => {}
		this.onDisconnected = null; // (reason) => {}
		this.onTokenExpire = null; // () => {}
	}

	// SDK 是否可用: script 标签加载失败时全局对象不存在;
	// 非 HTTPS 环境浏览器不给采集设备
	isEnable() {
		return typeof window.AgoraRTC !== 'undefined' && !!navigator.mediaDevices
			&& !!navigator.mediaDevices.getUserMedia;
	}

	// 初始化客户端并注册事件。mode rtc = 通话场景(与直播相对),
	// codec vp8 在老设备上兼容性比 h264/vp9 好, 与 App 端 call.html 一致。
	init() {
		if (this.client) {
			return;
		}
		this.client = window.AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
		this.client.on('user-published', this._handleUserPublished.bind(this));
		this.client.on('user-unpublished', this._handleUserUnpublished.bind(this));
		this.client.on('user-left', this._handleUserLeft.bind(this));

		// 声网侧的网络断开。autoReconnect 默认开着, DISCONNECTED 才是真的没救了
		this.client.on('connection-state-change', (cur, prev, reason) => {
			console.log(`[Agora] conn:${prev}->${cur}${reason ? '(' + reason + ')' : ''}`);
			if (cur === 'DISCONNECTED' && this.joined && this.onDisconnected) {
				this.onDisconnected(reason);
			}
		});

		// token 有效期 2 小时(服务端 agora.token-expire-seconds), 一通电话远达不到,
		// 真走到这里只能交给上层提示并结束
		this.client.on('token-privilege-did-expire', () => {
			this.onTokenExpire && this.onTokenExpire();
		});

		// 自动播放被浏览器拦下时, 借用户的首次点击手势重新 play()。
		// 只拆监听不重播是没用的: 必须在用户手势的同步上下文里重新调用。
		window.AgoraRTC.onAutoplayFailed = () => {
			console.log('[Agora] autoplay-blocked, 等待用户点击后重试');
			const retry = () => {
				document.removeEventListener('click', retry);
				autoplayRetryHandler = null;
				Object.keys(this.remoteUsers).forEach((uid) => {
					const user = this.remoteUsers[uid];
					try {
						user.audioTrack && user.audioTrack.play();
					} catch (e) {
						console.log('[Agora] autoplay重试失败', e);
					}
				});
			};
			autoplayRetryHandler = retry;
			document.addEventListener('click', retry);
		};
	}

	// 加入频道。token 只对 (channel + uid) 这一个组合有效,
	// uid 必须用服务端签发的那个(主叫固定 1, 被叫固定 2)。
	join(appId, channel, token, uid) {
		if (!appId || !channel || !token) {
			return Promise.reject(new Error('未拿到通话凭据'));
		}
		this.init();
		console.log(`[Agora] joining ch=${channel} uid=${uid}`);
		return this.client.join(appId, channel, token, uid).then(() => {
			this.joined = true;
		});
	}

	// 创建并发布本地音频。权限刚授予或上一通的采集刚释放时, 首次采集
	// 经常报 NOT_READABLE(设备还没就绪), 稍等重试一次通常就能起来,
	// 直接报错会白白丢掉一通能接通的通话 —— 与 call.html 的重试策略一致。
	publishAudio() {
		return this._createAudioTrackOnce().catch((e) => {
			const key = `${(e && (e.code || e.name)) || ''} ${(e && e.message) || ''}`;
			if (key.indexOf('NOT_READABLE') < 0 && key.indexOf('NotReadableError') < 0
				&& key.indexOf('TrackStartError') < 0) {
				throw e;
			}
			console.log('[Agora] 采集重试:', key);
			return new Promise((r) => setTimeout(r, 900)).then(() => this._createAudioTrackOnce());
		}).then(() => {
			return this.client.publish([this.localAudioTrack]);
		});
	}

	_createAudioTrackOnce() {
		return window.AgoraRTC.createMicrophoneAudioTrack(AUDIO_CONFIG).then((audioTrack) => {
			this.localAudioTrack = audioTrack;
		});
	}

	// 静音开关。setEnabled(false) 是真的停采集并通知对端, 比推静音帧省带宽
	setMuted(muted) {
		if (this.localAudioTrack) {
			try {
				this.localAudioTrack.setEnabled(!muted);
			} catch (e) {
				console.log('[Agora] 静音切换失败', e);
			}
		}
	}

	// 离开频道并释放资源。先关本地采集再退频道: close() 才真正释放麦克风,
	// 不释放的话下一通通话抢不到设备, 表现为 NOT_READABLE。
	leave() {
		if (this.localAudioTrack) {
			try { this.localAudioTrack.stop(); } catch (e) { /* 忽略 */ }
			try { this.localAudioTrack.close(); } catch (e) { /* 忽略 */ }
			this.localAudioTrack = null;
		}
		let leavePromise = Promise.resolve();
		if (this.client) {
			this.client.removeAllListeners();
			// 只有真的进过频道才 leave: 未入会时 leave() 会报错
			if (this.joined) {
				try {
					leavePromise = this.client.leave().catch(() => {});
				} catch (e) { /* 忽略 */ }
			}
			this.client = null;
		}
		this.joined = false;
		this.remoteUsers = {};
		// 移除可能残留的自动播放重试监听，避免离开后仍占用 document click 事件
		if (autoplayRetryHandler) {
			document.removeEventListener('click', autoplayRetryHandler);
			autoplayRetryHandler = null;
		}
		try { window.AgoraRTC.onAutoplayFailed = null; } catch (e) { /* 忽略 */ }
		return leavePromise;
	}

	// 对方推流了 → 订阅并播放。音频直接 play(), 声网自己管 audio 元素。
	// 服务端已强制语音模式, 视频轨不该出现, 真出现(旧客户端)也直接忽略。
	_handleUserPublished(user, mediaType) {
		if (mediaType !== 'audio') {
			return;
		}
		this.client.subscribe(user, mediaType).then(() => {
			console.log(`[Agora] subscribed audio from ${user.uid}`);
			user.audioTrack && user.audioTrack.play();
			this.remoteUsers[user.uid] = user;
			this.onRemoteUserJoined && this.onRemoteUserJoined(user);
		}).catch((e) => {
			console.error('[Agora] subscribe失败', e);
		});
	}

	_handleUserUnpublished(user, mediaType) {
		console.log(`[Agora] unpublished ${mediaType} from ${user.uid}`);
	}

	// 对方离开频道。正常挂断时信令(106)一般比这个先到, 这里是兜底:
	// 对方进程被杀/断网时只有频道事件, 没有挂断信令。
	_handleUserLeft(user) {
		console.log(`[Agora] user-left ${user.uid}`);
		delete this.remoteUsers[user.uid];
		this.onRemoteUserLeft && this.onRemoteUserLeft(user);
	}
}

export default AgoraVoiceClient;
